import { useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";

import { IGroupByListItem } from "models";

import useFeatureFlags, { getFieldGroupFlag } from "./useFeatureFlags";

export default function useCategories(): {
  categories: IGroupByListItem[];
  facilityCategories: IGroupByListItem[];
} {
  // Get category properties for column and header
  const categories = useSelector((state: RootState) => state.groupBy.categoryList);
  const facilityCategories = useSelector(
    (state: RootState) => state.groupBy.facilityFields
  );
  const { hasFeature } = useFeatureFlags();

  // EVA-10: field-group flags. Both lists are filtered here so every
  // consumer — WellCard's Well/Facility Ticket tabs and
  // PerformanceDataAnalyzer's column builder — gets the same display gating
  // from one place. A category (e.g. "Header", "Performance") whose
  // "<Category> Fields" flag is disabled is dropped; the facility side
  // follows the same flags as the well side, matching the data-table
  // column chooser (which already gates whatever categories it's handed,
  // facility included).
  const filteredCategories = useMemo(
    () =>
      (categories ?? []).filter((category) => {
        const flag = getFieldGroupFlag(category.name);
        return !flag || hasFeature(flag);
      }),
    [categories, hasFeature]
  );

  const filteredFacilityCategories = useMemo(
    () =>
      (facilityCategories ?? []).filter((category) => {
        const flag = getFieldGroupFlag(category.name);
        return !flag || hasFeature(flag);
      }),
    [facilityCategories, hasFeature]
  );

  return {
    categories: filteredCategories,
    facilityCategories: filteredFacilityCategories
  };
}
